import React from "react";
import {DamageCarrier, EntityMonstrousAbility, Monster, MonsterSpecies, MonsterType} from "../../../server/monster";
import {MonsterManualEntry} from "./MonsterManualEntry";

interface Props {
    monsters: Monster[];
    carriers: DamageCarrier[];
}

// ── Shared table styles ───────────────────────────────────────────────────────

const border = "1px solid #000";
const th: React.CSSProperties = {border, padding: "3px 6px", fontWeight: "bold", fontSize: 11, verticalAlign: "middle", backgroundColor: "#eee"};
const td: React.CSSProperties = {border, padding: "3px 6px", fontSize: 11, verticalAlign: "top"};

const sectionPage: React.CSSProperties = {
    height: "10in",
    overflow: "hidden",
    pageBreakAfter: "always",
    fontFamily: "Arial, sans-serif",
};

const sectionLabel: React.CSSProperties = {
    fontSize: 10,
    fontWeight: "bold",
    letterSpacing: 1.5,
    color: "#666",
    textTransform: "uppercase",
    margin: "0 0 4px 0",
};

const divider: React.CSSProperties = {
    borderTop: "2px solid #000",
    margin: "8px 0 12px 0",
};

// ── Grouping ──────────────────────────────────────────────────────────────────

interface TypeGroup  { type: MonsterType;    monsters: Monster[] }
interface SpeciesGroup { species: MonsterSpecies; types: TypeGroup[] }

function groupMonsters(monsters: Monster[]): SpeciesGroup[] {
    const speciesMap = new Map<number, SpeciesGroup>();
    const speciesOrder: number[] = [];

    for (const m of monsters) {
        const specId = (m.type?.species as any)?.id ?? 0;
        if (!speciesMap.has(specId)) {
            speciesMap.set(specId, {species: m.type.species, types: []});
            speciesOrder.push(specId);
        }
        const sg = speciesMap.get(specId)!;

        const typeId = m.type?.id ?? 0;
        let tg = sg.types.find(t => t.type.id === typeId);
        if (!tg) {
            tg = {type: m.type, monsters: []};
            sg.types.push(tg);
        }
        tg.monsters.push(m);
    }

    return speciesOrder.map(id => speciesMap.get(id)!);
}

// ── TOC entry types ───────────────────────────────────────────────────────────

interface TocEntry {
    kind: "species" | "type" | "monster";
    name: string;
    page: number;
    indent: number;
}

/**
 * Estimate how many printed pages a monster occupies.
 *
 * Page 1 is always exactly 1 page (fixed-height, overflow:hidden).
 * Page 2+ is a free-flowing block. We estimate its height from the number of
 * table rows, then divide by 10in (usable height with 0.5in margins).
 *
 * Heights used (conservative estimates at Arial 11pt):
 *   Fixed overhead: ~2.8in  (name header, stats table, weapons/intel text,
 *                             section headings, table header rows)
 *   Per data row:   ~0.22in
 *   Monstrous section heading adds ~0.5in when present
 */
function estimateMonsterPageCount(monster: Monster): number {
    const offCount = (monster.offensiveAbilities ?? []).length;
    const defCount = (monster.defensiveAbilities ?? []).length;

    const seenIds = new Set<number>();
    const monCount = [
        ...(monster.type?.species?.monstrousAbilities ?? []),
        ...(monster.type?.monstrousAbilities ?? []),
        ...(monster.monstrousAbilities ?? []),
    ].filter(e => {
        const id = e.ability.id as number;
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
    }).length;

    const FIXED_IN  = 2.8;
    const ROW_IN    = 0.22;
    const PAGE_H_IN = 10;

    // Each table always renders at least 1 row ("None" when empty)
    const offRows = Math.max(1, offCount);
    const defRows = Math.max(1, defCount);
    // Monstrous section only appears when there are monstrous abilities;
    // add 0.5in for the heading + table header that aren't in FIXED_IN
    const monRows = monCount > 0 ? monCount + (0.5 / ROW_IN) : 0;

    const page2Height = FIXED_IN + (offRows + defRows + monRows) * ROW_IN;
    const page2Pages  = Math.max(1, Math.ceil(page2Height / PAGE_H_IN));

    return 1 + page2Pages;
}

function buildToc(groups: SpeciesGroup[]): TocEntry[] {
    const entries: TocEntry[] = [];
    let page = 2; // page 1 is the TOC itself

    for (const sg of groups) {
        entries.push({kind: "species", name: sg.species.name, page, indent: 0});
        page++; // 1 page per species

        for (const tg of sg.types) {
            entries.push({kind: "type", name: tg.type.name, page, indent: 1});
            page++; // 1 page per type

            for (const m of tg.monsters) {
                entries.push({kind: "monster", name: m.name, page, indent: 2});
                page += estimateMonsterPageCount(m);
            }
        }
    }

    return entries;
}

// ── Monstrous abilities table (shared) ───────────────────────────────────────

const typeOrder: Record<string, number> = {WEAKNESS: 0, STRENGTH: 1, IMMUNITY: 2};

function MonstrousTable({abilities, title = "Monstrous Abilities"}: {abilities: EntityMonstrousAbility[]; title?: string}) {
    if (abilities.length === 0) return null;
    const sorted = [...abilities].sort((a, b) =>
        (typeOrder[a.ability.type] ?? 3) - (typeOrder[b.ability.type] ?? 3)
    );
    return (
        <>
            <p style={{fontWeight: "bold", fontSize: 13, margin: "12px 0 4px 0"}}>{title}</p>
            <table style={{width: "100%", borderCollapse: "collapse"}}>
                <colgroup>
                    <col style={{width: "30%"}}/><col style={{width: "20%"}}/><col style={{width: "50%"}}/>
                </colgroup>
                <tbody>
                <tr>
                    <td style={th}>Ability</td>
                    <td style={{...th, textAlign: "center"}}>Type</td>
                    <td style={th}>Call</td>
                </tr>
                {sorted.map((e, i) => (
                    <tr key={e.ability.id ?? i}>
                        <td style={td}>{e.ability.name}</td>
                        <td style={{...td, textAlign: "center"}}>{e.ability.type}</td>
                        <td style={td}>{e.ability.call}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </>
    );
}

// ── TOC Page ──────────────────────────────────────────────────────────────────

function TocPage({entries}: {entries: TocEntry[]}) {
    return (
        <div style={sectionPage}>
            <h1 style={{textAlign: "center", fontSize: 36, fontWeight: "bold", margin: "0 0 4px 0", fontFamily: "Arial, sans-serif"}}>
                AMA Monster Manual
            </h1>
            <p style={{textAlign: "center", fontSize: 14, color: "#444", margin: "0 0 16px 0", fontFamily: "Arial, sans-serif"}}>
                Table of Contents
            </p>
            <div style={divider}/>
            <div style={{fontFamily: "Arial, sans-serif", columns: "2", columnGap: "0.5in"}}>
                {entries.map((e, i) => {
                    const indentPx = e.indent * 20;
                    const isSpecies = e.kind === "species";
                    const isType    = e.kind === "type";
                    return (
                        <div key={i} style={{
                            display: "flex",
                            justifyContent: "space-between",
                            paddingLeft: indentPx,
                            marginBottom: isSpecies ? 6 : isType ? 2 : 1,
                            breakInside: "avoid",
                        }}>
                            <span style={{
                                fontSize: isSpecies ? 13 : isType ? 12 : 10,
                                fontWeight: isSpecies ? "bold" : isType ? "600" : "normal",
                                color: isSpecies ? "#000" : isType ? "#222" : "#444",
                                marginRight: 8,
                                flex: 1,
                            }}>
                                {e.name}
                            </span>
                            <span style={{
                                fontSize: isSpecies ? 13 : isType ? 12 : 10,
                                fontWeight: isSpecies ? "bold" : "normal",
                                color: "#444",
                                flexShrink: 0,
                            }}>
                                {e.page}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Species Page ──────────────────────────────────────────────────────────────

function SpeciesPage({species}: {species: MonsterSpecies}) {
    return (
        <div style={sectionPage}>
            <p style={sectionLabel}>Species</p>
            <h1 style={{fontSize: 32, fontWeight: "bold", margin: "0 0 8px 0", fontFamily: "Arial, sans-serif"}}>
                {species.name}
            </h1>
            <div style={divider}/>
            {species.description && (
                <>
                    <p style={{fontWeight: "bold", fontSize: 13, margin: "0 0 4px 0"}}>Description</p>
                    <p style={{fontSize: 11, margin: "0 0 12px 0", whiteSpace: "pre-wrap", lineHeight: 1.5}}>
                        {species.description}
                    </p>
                </>
            )}
            <MonstrousTable abilities={species.monstrousAbilities ?? []}/>
        </div>
    );
}

// ── Type Page ─────────────────────────────────────────────────────────────────

function TypePage({type}: {type: MonsterType}) {
    // Deduplicate monstrous abilities: species first, then type's own
    const seenIds = new Set<number>();
    const allMonstrous: EntityMonstrousAbility[] = [];
    for (const e of [...(type.species?.monstrousAbilities ?? []), ...(type.monstrousAbilities ?? [])]) {
        const id = e.ability.id as number;
        if (!seenIds.has(id)) { seenIds.add(id); allMonstrous.push(e); }
    }

    return (
        <div style={sectionPage}>
            <p style={sectionLabel}>
                Species: {type.species?.name ?? ""} &mdash; Type
            </p>
            <h1 style={{fontSize: 32, fontWeight: "bold", margin: "0 0 8px 0", fontFamily: "Arial, sans-serif"}}>
                {type.name}
            </h1>
            <div style={divider}/>

            {/* Stat badges */}
            <div style={{display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12, fontSize: 11, fontFamily: "Arial, sans-serif"}}>
                {type.combat_role && (
                    <span><strong>Combat Role:</strong> {type.combat_role}</span>
                )}
                <span><strong>General Use:</strong> {type.general_use ? "Yes" : "No"}</span>
                {type.maximum_fieldable != null && (
                    <span><strong>Max Fieldable:</strong> {type.maximum_fieldable}</span>
                )}
                {type.latex_based != null && (
                    <span><strong>Latex Based:</strong> {type.latex_based ? "Yes" : "No"}</span>
                )}
            </div>

            {type.description && (
                <>
                    <p style={{fontWeight: "bold", fontSize: 13, margin: "0 0 4px 0"}}>Description</p>
                    <p style={{fontSize: 11, margin: "0 0 12px 0", whiteSpace: "pre-wrap", lineHeight: 1.5}}>
                        {type.description}
                    </p>
                </>
            )}

            {type.costuming_requirements && (
                <>
                    <p style={{fontWeight: "bold", fontSize: 13, margin: "0 0 4px 0"}}>Costuming Requirements</p>
                    <p style={{fontSize: 11, margin: "0 0 12px 0", whiteSpace: "pre-wrap", lineHeight: 1.5}}>
                        {type.costuming_requirements}
                    </p>
                </>
            )}

            <MonstrousTable abilities={allMonstrous}/>
        </div>
    );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function MonsterManualBook({monsters, carriers}: Props) {
    const groups = groupMonsters(monsters);
    const tocEntries = buildToc(groups);

    return (
        <>
            <TocPage entries={tocEntries}/>
            {groups.map((sg, si) => (
                <React.Fragment key={sg.species.name + si}>
                    <SpeciesPage species={sg.species}/>
                    {sg.types.map((tg, ti) => (
                        <React.Fragment key={tg.type.name + ti}>
                            <TypePage type={tg.type}/>
                            {tg.monsters.map(m => (
                                // Wrapper div ensures next section always starts on a fresh page
                                // even when monster page 2 ends mid-page
                                <div key={m.id} style={{pageBreakAfter: "always"}}>
                                    <MonsterManualEntry monster={m} carriers={carriers}/>
                                </div>
                            ))}
                        </React.Fragment>
                    ))}
                </React.Fragment>
            ))}
        </>
    );
}
